/**
 * PET / COMPANHEIRO (10.5C) — orbe de cristal VOID procedural que flutua ao lado do
 * player, seguindo-o com suavidade (bob + rotação). Ganha nível com abates (GameProgress.petLevel):
 * amplia o alcance de coleta de essência e dá um pequeno bônus de dano ao Caçador (10.9L21).
 * 10.9L21 — quando há essência solta perto (`orbTarget`), o pet VOA até ela em vez de só
 * orbitar o jogador esperando ela chegar perto sozinha (Thiago: "ele não vai atrás dos recursos").
 */
import * as THREE from "three";

export class Pet {
  readonly group = new THREE.Group();
  private bob = 0;
  private readonly core: THREE.Mesh;
  private readonly ring: THREE.Mesh;
  private readonly ring2: THREE.Mesh;
  private readonly shards: THREE.Mesh[] = [];
  private visible = false;
  private readonly tmp = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    // 10.6F — o pet tinha 16 cm de núcleo e um aro de 26 cm: com a câmera a 27 m ele
    // simplesmente NÃO existia na tela. Dobrou de tamanho, ganhou halo, um segundo aro
    // cruzado e três lascas orbitando — agora dá para ver que você tem um companheiro.
    const coreMat = new THREE.MeshStandardMaterial({ color: 0xbdf3ff, emissive: 0x39b8ff, emissiveIntensity: 2.6, roughness: 0.2, metalness: 0.2, flatShading: true });
    this.core = new THREE.Mesh(new THREE.OctahedronGeometry(0.34, 0), coreMat);
    this.group.add(this.core);
    // casca externa (anel/gaiola) girando
    const shellMat = new THREE.MeshStandardMaterial({ color: 0x8f7bff, emissive: 0x6a3bff, emissiveIntensity: 1.5, roughness: 0.35, transparent: true, opacity: 0.75, flatShading: true });
    this.ring = new THREE.Mesh(new THREE.TorusGeometry(0.52, 0.055, 6, 18), shellMat);
    this.ring.rotation.x = Math.PI / 2;
    this.group.add(this.ring);
    const ring2 = new THREE.Mesh(new THREE.TorusGeometry(0.42, 0.04, 6, 16), shellMat);
    ring2.rotation.y = Math.PI / 2;
    ring2.rotation.z = 0.5;
    this.group.add(ring2);
    this.ring2 = ring2;
    // halo aditivo: mancha de luz que se lê de longe mesmo sob o sol
    const halo = new THREE.Mesh(
      new THREE.SphereGeometry(0.62, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0x5ed6ff, transparent: true, opacity: 0.2, depthWrite: false, blending: THREE.AdditiveBlending }),
    );
    this.group.add(halo);
    // 3 lascas orbitando (movimento é o que chama o olho)
    const shardMat = new THREE.MeshStandardMaterial({ color: 0xbdf3ff, emissive: 0x39b8ff, emissiveIntensity: 2.2, roughness: 0.25, flatShading: true });
    for (let i = 0; i < 3; i++) {
      const sh = new THREE.Mesh(new THREE.TetrahedronGeometry(0.11, 0), shardMat);
      this.group.add(sh);
      this.shards.push(sh);
    }
    const light = new THREE.PointLight(0x5ed6ff, 5, 7, 2);
    this.group.add(light);
    this.group.visible = false;
    this.group.position.set(0, 1.4, 0);
    scene.add(this.group);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.group.visible = v;
  }
  get isActive(): boolean {
    return this.visible;
  }

  /**
   * Segue o player: alvo = um pouco atrás/ao lado + altura de flutuação. Se `orbTarget` vier
   * preenchido (essência solta perto, ver EssenceManager.nearestOrbPos), o pet voa até ELA em
   * vez do ombro do jogador — o alcance de coleta (`GameProgress.petMagnetMult`) já faz a
   * essência ser puxada pra perto do pet quando ele chega, então parece que ele "buscou".
   */
  update(dt: number, playerPos: THREE.Vector3, facing: number, orbTarget?: THREE.Vector3 | null): void {
    if (!this.visible) return;
    this.bob += dt * 2.4;
    if (orbTarget) {
      this.tmp.set(orbTarget.x, 1.1 + Math.sin(this.bob) * 0.12, orbTarget.z);
      this.group.position.lerp(this.tmp, Math.min(1, dt * 9)); // 10.9L21 — mais rápido: "vai atrás"
    } else {
      // ponto-alvo: ombro esquerdo do player, atrás, flutuando
      const ox = Math.cos(facing) * -0.7 + Math.sin(facing) * -0.5;
      const oz = -Math.sin(facing) * -0.7 + Math.cos(facing) * -0.5;
      this.tmp.set(playerPos.x + ox * 1.25, 1.95 + Math.sin(this.bob) * 0.16, playerPos.z + oz * 1.25);
      // suavização (lerp) — o pet "persegue" sem grudar
      this.group.position.lerp(this.tmp, Math.min(1, dt * 6));
    }
    this.ring.rotation.z += dt * 2.2;
    this.ring2.rotation.x += dt * 1.7;
    this.core.rotation.y += dt * 1.6;
    // lascas orbitando em alturas diferentes
    for (let i = 0; i < this.shards.length; i++) {
      const a = this.bob * 0.9 + (i / this.shards.length) * Math.PI * 2;
      const sh = this.shards[i];
      sh.position.set(Math.cos(a) * 0.66, Math.sin(a * 1.6) * 0.22, Math.sin(a) * 0.66);
      sh.rotation.set(a * 1.4, a, 0);
    }
  }

  dispose(scene: THREE.Scene): void {
    scene.remove(this.group);
  }
}
